import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Layers, Check, X, ChevronRight } from 'lucide-react';

const InventoryCatalogPanel = ({
  items,
  itemSearch,
  setItemSearch,
  filteredItems,
  loading,
  catalogItemId,
  setCatalogItemId,
  setShowNewItemModal,
  itemDetail,
  editingItem,
  setEditingItem,
  handleDeleteItem,
  handleUpdateItem,
  setActiveTab,
  currentVersion,
  hasDraftChanges,
  resetDraftChanges,
  draftComponents,
  componentVersionMap,
  componentVersionLoading,
  handleDeleteComponent,
  updateDraftComponent,
  componentForm,
  handleComponentItemChange,
  setComponentForm,
  componentLoading,
  componentVersions,
  handleAddComponent,
  newVersion,
  setNewVersion,
  handleCreateVersion,
  renderCatalogComponents,
  catalogVersionComponents,
}) => {
  const [showAllItems, setShowAllItems] = useState(false);
  const visibleItems = showAllItems ? filteredItems : filteredItems.slice(0, 10);

  return (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <section className="space-y-4">
      <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm">
        <button
          type="button"
          onClick={() => setShowNewItemModal(true)}
          className="w-full rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 px-4 py-3 text-left transition hover:bg-white hover:shadow-sm"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Plus className="h-4 w-4" />
            Ajouter un item
          </div>
          <p className="mt-1 text-xs text-gray-500">Creez un item de catalogue.</p>
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase text-gray-500">Items</h2>
          <span className="text-xs text-gray-400">{items.length}</span>
        </div>
        <input
          type="text"
          placeholder="Rechercher"
          value={itemSearch}
          onChange={(e) => setItemSearch(e.target.value)}
          className="w-full rounded border px-3 py-2 text-sm"
        />
        <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
          {loading.items ? (
            <div className="text-sm text-gray-500">Chargement...</div>
          ) : (
            visibleItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setCatalogItemId(item.id)}
                className={`w-full rounded-xl border px-3 py-2 text-left text-sm transition ${
                  catalogItemId === item.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{item.name}</span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </div>
                {item.description && (
                  <p className="mt-1 text-xs text-gray-500 line-clamp-2">{item.description}</p>
                )}
              </button>
            ))
          )}
          {!loading.items && filteredItems.length === 0 && (
            <div className="text-sm text-gray-500">Aucun item</div>
          )}
        </div>
        {!loading.items && filteredItems.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAllItems((prev) => !prev)}
            className="text-xs text-slate-600 hover:text-slate-900"
          >
            {showAllItems
              ? 'Afficher moins'
              : `Afficher tout (${filteredItems.length})`}
          </button>
        )}
      </div>
    </section>

    <section className="lg:col-span-2 space-y-6">
      {!itemDetail ? (
        <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-6 text-sm text-slate-500">
          Selectionnez un item pour gerer ses versions et ses sous-items.
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Pencil className="h-4 w-4 text-gray-500" />
                {itemDetail.item.name}
              </h2>
              <div className="flex items-center gap-3">
                {itemDetail.item.purchase_url && (
                  <a
                    href={itemDetail.item.purchase_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Ouvrir le lien
                  </a>
                )}
                <button
                  type="button"
                  onClick={handleDeleteItem}
                  className="inline-flex items-center gap-2 text-sm text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </button>
              </div>
            </div>
            {editingItem && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">
                      Nom de l item
                    </div>
                    <input
                      type="text"
                      value={editingItem.name}
                      onChange={(e) =>
                        setEditingItem((prev) => ({ ...prev, name: e.target.value }))
                      }
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wide text-gray-400">
                      Lien (optionnel)
                    </div>
                    <input
                      type="text"
                      placeholder="https://"
                      value={editingItem.purchase_url || ''}
                      onChange={(e) =>
                        setEditingItem((prev) => ({
                          ...prev,
                          purchase_url: e.target.value,
                        }))
                      }
                      className="w-full rounded border px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wide text-gray-400">
                    Description
                  </div>
                  <textarea
                    value={editingItem.description || ''}
                    onChange={(e) =>
                      setEditingItem((prev) => ({ ...prev, description: e.target.value }))
                    }
                    className="w-full rounded border px-3 py-2 text-sm"
                    rows={3}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleUpdateItem}
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm text-white"
                  >
                    <Check className="h-4 w-4" />
                    Mettre a jour l item
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingItem(itemDetail.item)}
                    className="inline-flex items-center gap-2 rounded border px-3 py-2 text-sm"
                  >
                    <X className="h-4 w-4" />
                    Reinitialiser les champs
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('stock')}
                    className="ml-auto text-sm text-blue-600 hover:text-blue-800"
                  >
                    Aller au stock
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-sm space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase text-gray-500 flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Versions
              </h3>
            </div>
            <div className="text-xs text-gray-500">
              Modifiez les sous-items puis sauvegardez pour creer une nouvelle version.
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-slate-50 p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {currentVersion
                      ? `Version actuelle - V${currentVersion.version_number}${
                          currentVersion.label ? ` - ${currentVersion.label}` : ''
                        }`
                      : 'Aucune version'}
                  </div>
                  {currentVersion?.description && (
                    <div className="text-xs text-slate-500">
                      {currentVersion.description}
                    </div>
                  )}
                  {!currentVersion && (
                    <div className="text-xs text-slate-500">
                      Ajoutez des sous-items pour creer la premiere version.
                    </div>
                  )}
                </div>
                {hasDraftChanges && (
                  <button
                    type="button"
                    onClick={resetDraftChanges}
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-white"
                  >
                    Reinitialiser les changements
                  </button>
                )}
              </div>

              <div className="space-y-2">
                <div className="text-[10px] uppercase tracking-wide text-gray-400">
                  Sous-items actuels
                </div>
                {draftComponents.length === 0 && (
                  <div className="text-xs text-slate-400">Aucun sous-item.</div>
                )}
                {draftComponents.map((component) => {
                  const versionKey = String(component.child_item_id || '');
                  const versionOptions = componentVersionMap[versionKey] || [];
                  const isLoadingVersions = componentVersionLoading[versionKey];
                  return (
                    <div
                      key={component.id}
                      className="rounded-lg border border-slate-200/70 bg-white p-3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-xs font-semibold text-slate-800">
                            {component.child_item_name ||
                              `Item #${component.child_item_id}`}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {component.child_version_number
                              ? `Version: V${component.child_version_number} ${
                                  component.child_version_label || ''
                                }`
                              : 'Sans version'}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeleteComponent(component.id)}
                          className="text-xs text-rose-600 hover:text-rose-700"
                        >
                          Supprimer
                        </button>
                      </div>
                      <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-3">
                        <select
                          value={component.child_version_id || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            const nextVersion = versionOptions.find(
                              (version) => String(version.id) === String(value),
                            );
                            updateDraftComponent(component.id, {
                              child_version_id: value || '',
                              child_version_number: nextVersion?.version_number || null,
                              child_version_label: nextVersion?.label || '',
                            });
                          }}
                          className="rounded border px-2 py-2 text-sm"
                          disabled={isLoadingVersions}
                        >
                          <option value="">
                            {isLoadingVersions ? 'Chargement...' : 'Sans version'}
                          </option>
                          {versionOptions.map((versionOption) => (
                            <option key={versionOption.id} value={versionOption.id}>
                              V{versionOption.version_number} {versionOption.label || ''}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min="1"
                          value={component.quantity}
                          onChange={(e) =>
                            updateDraftComponent(component.id, {
                              quantity: e.target.value,
                            })
                          }
                          className="rounded border px-2 py-2 text-sm"
                          placeholder="Quantite"
                        />
                        <input
                          type="text"
                          value={component.notes}
                          onChange={(e) =>
                            updateDraftComponent(component.id, {
                              notes: e.target.value,
                            })
                          }
                          className="rounded border px-2 py-2 text-sm"
                          placeholder="Note (optionnel)"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <form
                onSubmit={handleAddComponent}
                className="rounded-xl border border-slate-200 bg-white p-3 space-y-2"
              >
                <div className="text-[10px] uppercase tracking-wide text-gray-400">
                  Ajouter un sous-item
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                  <select
                    value={componentForm.child_item_id}
                    onChange={(e) => handleComponentItemChange(e.target.value)}
                    className="rounded border px-2 py-2 text-sm"
                    required
                  >
                    <option value="">Choisir un item</option>
                    {items
                      .filter((item) => item.id !== itemDetail.item.id)
                      .map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  <select
                    value={componentForm.child_version_id}
                    onChange={(e) =>
                      setComponentForm((prev) => ({
                        ...prev,
                        child_version_id: e.target.value,
                      }))
                    }
                    className="rounded border px-2 py-2 text-sm"
                    disabled={!componentForm.child_item_id || componentLoading}
                  >
                    <option value="">
                      {componentLoading ? 'Chargement...' : 'Version (optionnel)'}
                    </option>
                    {componentVersions.map((versionOption) => (
                      <option key={versionOption.id} value={versionOption.id}>
                        V{versionOption.version_number} {versionOption.label || ''}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    value={componentForm.quantity}
                    onChange={(e) =>
                      setComponentForm((prev) => ({
                        ...prev,
                        quantity: e.target.value,
                      }))
                    }
                    className="rounded border px-2 py-2 text-sm"
                    placeholder="Quantite"
                  />
                </div>
                <textarea
                  value={componentForm.notes}
                  onChange={(e) =>
                    setComponentForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  className="rounded border px-2 py-2 text-sm"
                  rows={2}
                  placeholder="Note (optionnel)"
                />
                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    className="rounded bg-blue-600 px-3 py-2 text-xs text-white hover:bg-blue-700"
                  >
                    Ajouter
                  </button>
                </div>
              </form>

              <form
                onSubmit={handleCreateVersion}
                className="rounded-xl border border-slate-200 bg-white p-3 space-y-3"
              >
                <div className="text-[10px] uppercase tracking-wide text-gray-400">
                  Nouvelle version
                </div>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <input
                    type="text"
                    placeholder="Label (ex: v1.2)"
                    value={newVersion.label}
                    onChange={(e) =>
                      setNewVersion((prev) => ({ ...prev, label: e.target.value }))
                    }
                    className="rounded border px-3 py-2 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Note de version"
                    value={newVersion.description}
                    onChange={(e) =>
                      setNewVersion((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    className="rounded border px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="submit"
                    disabled={!hasDraftChanges}
                    className={`inline-flex items-center gap-2 rounded px-3 py-2 text-sm text-white ${
                      hasDraftChanges
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-slate-300'
                    }`}
                  >
                    <Plus className="h-4 w-4" />
                    Sauvegarder la nouvelle version
                  </button>
                  {!hasDraftChanges && (
                    <span className="text-[11px] text-gray-400">
                      Aucun changement detecte.
                    </span>
                  )}
                </div>
              </form>
            </div>

            <div className="rounded-xl border border-slate-200/70 bg-white p-4 space-y-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-400">
                Historique des versions
              </div>
              {itemDetail.versions.length === 0 && (
                <div className="text-xs text-slate-400">Aucune version.</div>
              )}
              {itemDetail.versions.map((version) => (
                <div
                  key={version.id}
                  className="rounded-xl border border-slate-200/70 bg-slate-50 px-3 py-3"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        V{version.version_number}{' '}
                        {version.label ? `- ${version.label}` : ''}
                      </div>
                      {version.description && (
                        <div className="text-xs text-slate-500">{version.description}</div>
                      )}
                    </div>
                    {currentVersion?.id === version.id && (
                      <span className="rounded-full bg-blue-100 px-2 py-1 text-[10px] text-blue-700">
                        Actuelle
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs">
                    {renderCatalogComponents(
                      catalogVersionComponents[version.id] || version.components || [],
                      0,
                      [version.id],
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  </div>
  );
};

export default InventoryCatalogPanel;
